// Japanese (です/ます) copy for /preview-first-uploads, translated from copy.en.ts. Same shape (checked by
// copy.test.ts). The second pass is always "オリジナル" (never "フル画質"). The uploader's own labels are in
// English, so they're quoted as it shows them.
import type { PreviewFirstCopy } from "./copy.en";

const ja: PreviewFirstCopy = {
  seo: {
    title: "プレビュー先行アップロード（すべてのプラン） | Photohawk",
    description:
      "PhotohawkのWebアップローダーの新機能（すべてのプラン）：各写真の小さなプレビューを先にアップロードするので、オリジナルをバックグラウンドで送りながら、ギャラリーを数分で公開し、販売や共有を始められます。",
  },
  hero: {
    badge: "新機能",
    eyebrow: "Webアップローダー · すべてのプラン",
    title: "ギャラリーを、数時間ではなく",
    titleHighlight: "数分で公開",
    subtitle:
      "Webアップローダーは、すべての写真の小さなプレビューを先に送るようになりました。ギャラリーはそのプレビューで公開できるので、オリジナルがバックグラウンドでアップロードされている間も、購入者は自分の写真を見つけて、購入し、共有できます。",
    ctaPrimary: "アップローダーを開く",
    ctaSecondary: "仕組みを見る",
    uploaderLink: "Webアップローダーの新機能をすべて見る",
  },
  steps: {
    heading: "仕組み",
    subtitle: "プレビュー先行アップロードは、すべてのプランのWebアップローダーで使えます。写真を追加すると、それぞれの方法でかかる時間が表示されるので、「Previews first, originals later」を選びます。",
    stepLabel: "ステップ{n}",
    items: [
      {
        id: "add",
        title: "写真を追加",
        body: "いつもどおり、写真やフォルダごとドロップします。ブラウザがお使いのパソコン上で、写真ごとに小さなプレビューを作成します。",
      },
      {
        id: "previews",
        title: "プレビューを先にアップロード",
        body: "プレビューは小さいので、会場の回線が遅くてもすばやくアップロードできます。",
      },
      {
        id: "live",
        title: "ギャラリーを公開",
        body: "プレビューのアップロードが終わったらすぐに公開できます。「Publish the gallery as soon as the previews are up」にチェックを入れておけば、自動で公開されます。購入者はすぐに閲覧、検索、購入、共有ができます。",
      },
      {
        id: "full",
        title: "オリジナルが続けて届く",
        body: "オリジナルはバックグラウンドでアップロードが続き、同じ写真に反映されます。ダウンロードやプリントには必ずオリジナルが使われます。",
      },
    ],
    timeline: {
      label: "アップロードの例（開始から完了まで）",
      previews: "プレビュー",
      previewsTime: "数分",
      live: "ギャラリー公開",
      full: "オリジナル",
      fullTime: "バックグラウンドで",
    },
  },
  buyers: {
    heading: "購入者に見える画面",
    subtitle: "新しく覚えることはありません。購入者には、いつものギャラリーが、より早く表示されます。",
    items: [
      {
        id: "protected",
        title: "いつもどおり透かし入りで保護",
        body: "購入者が閲覧するのは、これまでと同じ透かし入りのプレビューです。ギャラリーでプレビュー保護を使っている場合も、同じように機能します。",
      },
      {
        id: "search",
        title: "自分の写真がすぐに見つかる",
        body: "セルフィー検索とゼッケン検索はプレビューで機能するので、ギャラリーが公開されたらすぐに自分の写真を見つけられます。",
      },
      {
        id: "delivery",
        title: "オリジナルを自動でお届け",
        body: "購入や無料ダウンロードでプレビューが届くことはありません。オリジナルが届く前に誰かが写真を購入またはダウンロードすると、そのオリジナルがキューの先頭に回り、準備ができしだいお届けします。",
      },
    ],
  },
  strip: {
    heading: "アップロード中も状況をチェック",
    body: "プレビューとオリジナルのアップロード中は、アップローダー上部の帯にギャラリーの状況が表示されます。公開済みかどうか、残りのオリジナルの数、購入された写真が先頭に回ったタイミングがわかります。ギャラリーの公開後は、直近30分間の訪問者数も表示され、ワークスペースのオーナーと管理者には、アップロード開始以降の新しい注文も表示されます。",
    exampleLabel: "例",
    live: "プレビューで公開中",
    orders: "開始以降の注文 3件",
    visitors: "直近30分間の訪問者 42人",
    left: "残りのオリジナル 1,250枚",
    jumped: "注文された2枚の写真のオリジナルが先頭へ",
  },
  faq: {
    heading: "よくある質問",
    items: [
      {
        q: "アップローダーは開いたままにしておく必要がありますか？",
        a: "オリジナルのアップロードが終わるまでは、開いておくことをおすすめします。残りの数は帯に表示されます。途中で閉じても、何も失われません。ギャラリーはプレビューで公開されたままで、次にそのギャラリーをアップローダーで開くと、まだ待機中の写真の数が表示されます。同じフォルダをもう一度追加すれば（後から自宅で追加しても構いません）、足りないオリジナルだけが送信されます。",
      },
      {
        q: "オリジナルが届く前に写真が購入されたら？",
        a: "そのまま購入できます。アップローダーが動いている間は、その写真のオリジナルがキューの先頭に回ります。注文メールはファイルの到着を待ち、届いてから数分以内に送信されるので、購入者に誤ってプレビューが届くことはありません。アップローダーを閉じていた場合は、フォルダをもう一度追加するまで注文は待機します。購入者が待っている写真はアップローダーに表示され、それらが優先して送信されます。",
      },
      {
        q: "無料ダウンロードのギャラリーでも使えますか？",
        a: "はい。プレビューのアップロードが終わればすぐに閲覧、検索、ダウンロードができ、ダウンロードでプレビューが届くことはありません。オリジナルが届く前に誰かが写真をダウンロードすると、そのオリジナルがキューの先頭に回ります。ダウンロード画面にはオリジナルをアップロード中であることが表示され、ファイルが届いた時点でダウンロードできるようになります。メールでのダウンロードもその時点で送信されます。ダウンロードは1回としてカウントされます。",
      },
      {
        q: "セルフィー検索とゼッケン検索はプレビューで使えますか？",
        a: "はい。顔とゼッケン番号はプレビューから検出されるので、プレビューがアップロードされればすぐに検索できます。アップローダーを開いている間は、検索の精度を上げるため、オリジナルから切り出した、より鮮明なクローズアップも送信します。",
      },
      {
        q: "どのプランで使えますか？",
        a: "すべてのプランで使えます（Liteを含む）。以前にワークスペースでプレビュー先行アップロードを使っていた場合、それらの写真は通常どおり完了します。同じフォルダをもう一度追加すればオリジナルが送信され、その写真の注文やダウンロードも引き続き届きます。",
      },
      {
        q: "追加料金はかかりますか？",
        a: "かかりません。すべてのプランに含まれており、写真は1枚として数えられます。プレビューとそのオリジナルは同じ写真です。唯一の違いは、回線で送るデータ量が全体で少し増えることです（約5%増）。",
      },
      {
        q: "FTPやPhotohawk Studioでも使えますか？",
        a: "現時点では使えません。プレビュー先行アップロードはWebアップローダーの機能です。FTPやPhotohawk Studioから送った写真は、これまでどおりオリジナルが一度にアップロードされます。",
      },
      {
        q: "最初からオンになっていますか？",
        a: "いいえ。写真を追加するたびに、Webアップローダーはプレビュー先行あり・なしでかかる時間を表示し、オリジナルをそのまま送る方法が選択された状態になります。ギャラリーを早く公開したいときは「Previews first, originals later」を選んでください。選択は次回のために記憶されます。小さいアップロード解像度（「Large」または「Web」）では縮小したファイルだけがアップロードされるため、プレビュー先行は使われません。",
      },
    ],
  },
  cta: {
    heading: "次のアップロードでお試しください",
    body: "プレビュー先行アップロードは、すべてのプランのWebアップローダーで使えます。写真を追加してプレビュー先行を選べば、ギャラリーを数分で公開できます。",
    button: "アップローダーを開く",
  },
};

export default ja;
